/**
 * Color helpers for server-supplied category colors. The server hands us an arbitrary
 * `colorHex` per project/category, so any surface filled with it has to pick its own
 * foreground color at runtime — these are the shared rules for doing that.
 */

/**
 * Relative luminance (WCAG) of a hex color, 0 (black) to 1 (white).
 * Accepts `#rrggbb` or `rrggbb`.
 */
export function luminance(hex: string): number {
  const h = hex.startsWith('#') ? hex : `#${hex}`;
  const r = parseInt(h.slice(1, 3), 16) / 255;
  const g = parseInt(h.slice(3, 5), 16) / 255;
  const b = parseInt(h.slice(5, 7), 16) / 255;
  const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/** Brand charcoal, for the inline-style paths that can't use the Tailwind token. */
const BRAND_CHARCOAL = '#444444';

/**
 * Foreground color to use on top of a `colorHex` fill: white on dark fills, brand
 * charcoal on light ones. Same threshold `ScheduleCellChip` has always used, so mobile
 * cards and the desktop grid stay visually consistent.
 */
export function readableTextColor(hex: string): string {
  return luminance(hex) < 0.4 ? 'white' : BRAND_CHARCOAL;
}
